// Combobox for long lists (MCC). Typing filters; the panel stays empty
// until the user types at least one character. Values commit only from
// the list (`allowCustomValue` is false).
//
// Enums (state, account type, titles) use `SelectField` instead — no
// typing, button opens the full list.
//
// Keyboard model:
//   - Focus the input: nothing happens (don't pop the panel on focus
//     alone — operators tab through forms).
//   - Type or click: panel opens, options filter by prefix.
//   - ArrowDown / ArrowUp: move highlight.
//   - Enter: select highlighted option.
//   - Escape: close panel without changing the value.
//   - Tab: close panel and move on (no implicit select on Tab).
//
// ARIA: combobox role on the wrapper + listbox role on the panel +
// aria-activedescendant pointing at the highlighted option's id.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export interface SearchableOption<T extends string = string> {
  value: T;
  label: string;
  /** Secondary text shown beneath the label (e.g., MCC description). */
  hint?: string;
}

function prefixMatch(haystack: string, q: string): boolean {
  const h = haystack.toLowerCase();
  if (h.startsWith(q)) return true;
  return h.split(/[^a-z0-9]+/).some((part) => part.startsWith(q));
}

interface Props<T extends string> {
  id?: string;
  value: T | "";
  options: readonly SearchableOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  /** Override the filter. Empty query should return [] (D4). */
  customFilter?: (
    query: string,
    options: readonly SearchableOption<T>[],
  ) => SearchableOption<T>[];
  renderItem?: (option: SearchableOption<T>) => React.ReactNode;
  required?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  inputMode?: "text" | "numeric";
  maxLength?: number;
  disabled?: boolean;
  selectedLabel?: string;
}

export function SearchableSelect<T extends string>({
  id,
  value,
  options,
  onChange,
  placeholder = "Type to search…",
  customFilter,
  renderItem,
  required,
  ariaInvalid,
  ariaDescribedBy,
  ariaLabel,
  inputMode = "text",
  maxLength,
  disabled = false,
  selectedLabel,
}: Props<T>) {
  const autoId = useId();
  const rootId = id ?? `ss-${autoId}`;
  const listboxId = `${rootId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (customFilter) return customFilter(query, options);
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return options.filter(
      (o) =>
        prefixMatch(o.label, q) ||
        prefixMatch(o.value, q) ||
        (o.hint ? prefixMatch(o.hint, q) : false),
    );
  }, [query, options, customFilter]);

  useEffect(() => {
    setHighlight(0);
  }, [filtered.length]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(
      `#${rootId}-opt-${highlight}`,
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open, rootId]);

  function commit(option: SearchableOption<T>) {
    onChange(option.value);
    setOpen(false);
    setQuery("");
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (filtered.length === 0) return;
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) commit(opt);
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setQuery("");
      }
    } else if (e.key === "Tab") {
      if (open) {
        setOpen(false);
        setQuery("");
      }
    }
  }

  const displayValue = open ? query : selected?.label ?? selectedLabel ?? "";
  const emptyMessage = query.trim() ? "No matches." : "Type to search…";

  return (
    <div
      ref={wrapperRef}
      className={`relative ${disabled ? "opacity-50" : ""}`}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-controls={listboxId}
      aria-owns={listboxId}
    >
      <div className="relative">
        <input
          ref={inputRef}
          id={rootId}
          type="text"
          className="input pr-9"
          value={displayValue}
          placeholder={selected || selectedLabel ? "" : placeholder}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered[highlight]
              ? `${rootId}-opt-${highlight}`
              : undefined
          }
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedBy}
          aria-label={ariaLabel}
          aria-required={required || undefined}
          autoComplete="off"
          inputMode={inputMode}
          maxLength={maxLength}
          disabled={disabled}
          onFocus={(e) => {
            e.target.select();
          }}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.target.value);
          }}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-muted"
        />
      </div>
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="absolute z-30 mt-1.5 max-h-72 w-full overflow-auto rounded-lg border border-brand-border bg-brand-surface-2 p-1 shadow-pop"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-brand-muted">
              {emptyMessage}
            </li>
          ) : (
            filtered.map((opt, idx) => (
              <li
                key={opt.value}
                id={`${rootId}-opt-${idx}`}
                role="option"
                aria-selected={opt.value === value}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(opt);
                }}
                className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
                  idx === highlight
                    ? "bg-brand-blue/25 text-white"
                    : "text-brand-text"
                }`}
              >
                {renderItem ? (
                  renderItem(opt)
                ) : (
                  <>
                    <div>{opt.label}</div>
                    {opt.hint && (
                      <div className="text-xs text-brand-muted">{opt.hint}</div>
                    )}
                  </>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
